import { ButtonGroup, TextButton } from '@oneaudi/unified-web-components';
import { useCallback } from 'react';
import { PriceSection } from '~/app/compositions/content/components/price-section';
import { SummarySection, TopSection } from '~/app/compositions/content/configuration-overview-builder';
import {
	ConfigurationImagesStyle,
	ConfigurationImageWrapperStyle,
	ConfigurationOverviewStyle,
	ConfigurationSectionWrapperStyle,
	ConfigurationSummaryStyle,
} from '~/app/compositions/content/configuration-overview-style';
import { SummaryCtaButton } from '~/app/compositions/content/service-footer/components/user-marketing/main-cta-button';
import { useDialogs } from '~/app/hooks/use-dialogs';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import { useVATConfig } from '~/app/market-config/use-vat-config';
import { StockCarEnabled, useStockCarDialog } from '~/app/pages/configurator/components/dialogs/stock-car-dialog';
import { useCatalog, useConfigurator } from '~/graphql/hooks/use-configurator';
import { useCurrencyFormatter } from '~/hooks/use-currency-formatter';

import { useMarketUiConfig } from '~/app/market-config/use-market-ui-config';
import { useSelectedEquipment } from '~/app/pages/summary/hooks/use-selected-equipment';
import { useImages } from '~/graphql/hooks/use-images';

import { GPSRSection } from '~/app/compositions/content/gpsr';
import { AutoFlowGrid } from '~/app/pages/components/auto-flow-grid';
import { ImageViewsEnum } from '~/app/pages/configurator/models/image-views';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { checkEventActionUrl } from '~/graphql/utils/check-event-action-url';
import GetUrlFromImageSet from '~/graphql/utils/get-from-image-set';
import { DATA_TEST_ID } from '~/test/data-test-id';

export function ConfigurationOverviewLegalGB() {
	const { openDialog } = useDialogs();
	const { isLoading } = useConfigurator();
	const { priceSummary } = useCatalog();
	const vatConfig = useVATConfig();
	const { formatCurrency } = useCurrencyFormatter();
	const { i18n } = useI18n();
	const { config } = useMarketConfig();
	const { showStockCarDialog } = useStockCarDialog(config.stockCarConfig.geoFilter);

	const { consumptionAndEmissionSummary } = useMarketUiConfig();

	const { imagesByProfileId } = useImages();
	const {
		configuration: { featuresAndPackages },
	} = useSelectedEquipment();
	const [color, seats] = ['Exterior_Colors', ':Interior_Tiles'].map((id) =>
		featuresAndPackages.find((feature) => feature.id.includes(id)),
	);
	let priceRemark: string | null = null;

	if (config.useStaticPriceDateRemark) {
		priceRemark = config.useSummaryAlternativePriceDateRemark
			? i18n('ngc.summary.alternativeRemark')
			: i18n('ngc.summary.remark');
	} else if (priceSummary?.priceDate?.text) {
		priceRemark = priceSummary?.priceDate?.text;
	}

	const impressionContainerRef = useCallback(
		(node: HTMLElement) => {
			if (node !== null && !isLoading) {
				appTrackingService.summaryImpression({
					node,
					eventName: 'configurator (config overview) - impression',
					componentName: 'configuration overview',
				});
			}
		},
		[isLoading],
	);

	const impressionCTARef = useCallback(
		(node: HTMLDivElement) => {
			if (node !== null && !isLoading) {
				const buttonNodes = Array.from(node.childNodes[0].childNodes || []) as HTMLElement[];
				appTrackingService.summaryImpression({
					node,
					eventName: 'configurator (config overview buttons) - impression',
					componentName: 'configuration overview',
					relatedElements: buttonNodes.map((buttonNode, index) => ({
						elementName: 'cta',
						elementValue: `${index + 1}-${buttonNode.innerText}`,
					})),
				});
			}
		},
		[isLoading],
	);

	const trackClickEvent = useCallback((eventName: string, label: string, url?: string) => {
		const buttonsContainerRef = document.querySelector('.buttons-group-cta');
		const buttonNodes = buttonsContainerRef?.getElementsByTagName('button');
		const nodeIndex = Array.from(buttonNodes || [])?.findIndex((buttonNode) => buttonNode.innerText === label);

		if (buttonNodes && nodeIndex !== -1) {
			appTrackingService.summaryClickEvent({
				eventAction: checkEventActionUrl(url || ''),
				eventName: `configurator (configuration overview) - click on ${eventName}`,
				componentName: 'configurator-configuration-overview',
				label,
				targetURL: url,
				elementName: 'button',
				relatedElements: [
					{
						elementName: 'cta',
						elementValue: `${nodeIndex + 1}-${buttonNodes[nodeIndex].id}`,
					},
				],
			});
		}
	}, []);

	return (
		<ConfigurationOverviewStyle
			ref={impressionContainerRef}
			data-testid={DATA_TEST_ID.SUMMARY_CONFIGURATION_OVERVIEW_CONTAINER}>
			<TopSection />
			<ConfigurationSummaryStyle>
				<ConfigurationSectionWrapperStyle>
					<SummarySection />
					<PriceSection
						totalPrice={formatCurrency(priceSummary?.totalPrice, vatConfig.totalPrice) ?? ''}
						monthlyRate={null}
						remark={priceRemark}
						showSpecialOffers={config.specialOfferUrl.length > 0}
					/>
					<div ref={impressionCTARef}>
						<ButtonGroup className="buttons-group-cta" orientation="vertical">
							{config.showPrices && (
								<TextButton
									id="price details"
									variant="secondary"
									size={'md'}
									isFullWidth
									data-testid={DATA_TEST_ID.SUMMARY_PRICE_DETAIL_BUTTON}
									onClick={() => {
										openDialog('priceDetails');
										trackClickEvent(
											'open price details layer',
											i18n('ngc.actions.showPriceDetails'),
										);
									}}>
									{i18n('ngc.actions.showPriceDetails')}
								</TextButton>
							)}
							<SummaryCtaButton
								trackClickEvent={trackClickEvent}
								variant="secondary"
								size="md"
								isSecondCta="false"
								isFullWidth
							/>
							{StockCarEnabled() && (
								<TextButton
									id="Car Matching"
									className="main-cta-button"
									isFullWidth
									variant="secondary"
									onClick={() => {
										showStockCarDialog();
										trackClickEvent('stock car matching', i18n('ngc.stockcar.trigger'));
									}}
									size={'md'}
									data-testid={DATA_TEST_ID.STOCK_CAR_BUTTON}>
									{i18n('ngc.stockcar.trigger')}
								</TextButton>
							)}
						</ButtonGroup>
					</div>
				</ConfigurationSectionWrapperStyle>
				<ConfigurationImageWrapperStyle>
					<ConfigurationImagesStyle>
						<AutoFlowGrid
							direction="bigImageFirst"
							sources={[
								imagesByProfileId[ImageViewsEnum.EXTERIOR_REAR_3_4],
								GetUrlFromImageSet(
									color?.families?.[0]?.items?.[0]?.imageSets,
									'outer_tile_hd_square',
								) || '//about:blank',
								imagesByProfileId[ImageViewsEnum.EXTERIOR_WHEELS_RIM],
							]}
						/>
						<AutoFlowGrid
							direction="smallImagesFirst"
							sources={[
								imagesByProfileId[ImageViewsEnum.LIGHTS_FRONT_HEADLIGHTS],
								GetUrlFromImageSet(
									seats?.families?.[0]?.items?.[0]?.imageSets,
									'interior_tile_button',
								) || '//about:blank',
								imagesByProfileId[ImageViewsEnum.INTERIOR_DASHBOARD_DASHBOARD],
							]}
						/>
						{consumptionAndEmissionSummary}
					</ConfigurationImagesStyle>
				</ConfigurationImageWrapperStyle>
			</ConfigurationSummaryStyle>
			<GPSRSection />
		</ConfigurationOverviewStyle>
	);
}
